/* @jsx h */
import { defineComponent, ref, onMounted, h } from "vue";
import { useRouter } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { logout as logoutRequest } from "@app/api/client";

import { useCustomer } from "@app/vue/providers/Auth";
import { ListRowsSkeleton } from "./AccountSkeleton";

export const ProfileSubpage = defineComponent({
  setup() {
    const auth = useCustomer();
    const customer = auth.customer;
    const isLoading = ref(true);
    const error = ref<string | null>(null);
    const { t } = useLocalization();
    const router = useRouter();

    const loggingOut = ref(false);
    const handleLogout = async () => {
      if (loggingOut.value) return;
      loggingOut.value = true;
      try {
        await logoutRequest();
        await auth.refresh();
        void router.push("/");
      } catch (err) {
        console.error("Logout failed:", err);
      } finally {
        loggingOut.value = false;
      }
    };

    const loadCustomerData = async () => {
      try {
        isLoading.value = true;
        const data = await auth.refresh();
      } catch (err) {
        error.value = t("customer.account.failed_to_load_profile");
        console.error("Error loading customer session:", err);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      loadCustomerData();
    });

    return () => {
      // The session is usually already known; only block on the refresh when
      // there is nothing to show yet.
      if (isLoading.value && !customer.value) {
        return (
          <div class="space-y-2">
            <div class="skeleton mx-1 h-3 w-36 rounded" aria-hidden="true" />
            <ListRowsSkeleton rows={4} />
          </div>
        );
      }

      if (error.value) {
        return (
          <div class="alert alert-error">
            <span class="material-symbols-outlined">error</span>
            <span>{error.value}</span>
          </div>
        );
      }

      if (!customer.value) {
        return (
          <div class="text-center py-8">
            <p class="text-base-content/60">
              {t("customer.account.no_profile")}
            </p>
          </div>
        );
      }

      const isRegistered = !customer.value.anon && "name" in customer.value;
      const registered = isRegistered
        ? (customer.value as { name?: string; email?: string })
        : null;

      const renderRow = (
        icon: string,
        label: string,
        value: JSX.Element | string | undefined,
        valueClass = "",
      ) => (
        <div class="flex items-center gap-3 px-4 py-3.5">
          <span
            class="material-symbols-outlined grid size-9 shrink-0 place-items-center rounded-xl bg-base-100 text-xl text-base-content/55 shadow-sm"
            aria-hidden="true"
          >
            {icon}
          </span>
          <div class="min-w-0 flex-1">
            <p class="text-xs text-base-content/50">{label}</p>
            <div class={["mt-0.5 text-[15px] font-medium", valueClass]}>
              {value}
            </div>
          </div>
        </div>
      );

      return (
        <div class="space-y-5">
          {customer.value.anon && (
            <div class="flex items-start gap-3 rounded-2xl bg-primary/5 p-4">
              <span
                class="material-symbols-outlined grid size-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-xl text-primary"
                aria-hidden="true"
              >
                info
              </span>
              <div>
                <h4 class="font-semibold text-base-content">
                  {t("customer.account.guest_account")}
                </h4>
                <p class="mt-1 text-sm text-base-content/60">
                  {t("customer.account.guest_account_copy")}
                </p>
              </div>
            </div>
          )}

          <section>
            <h3 class="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-base-content/45">
              {t("customer.account.account_information")}
            </h3>
            <div class="divide-y divide-base-content/5 overflow-hidden rounded-2xl bg-base-200/60">
              {registered &&
                renderRow(
                  "badge",
                  t("customer.account.name"),
                  registered.name,
                  "truncate",
                )}
              {registered &&
                renderRow(
                  "mail",
                  t("customer.account.email"),
                  registered.email,
                  "break-all",
                )}
              {renderRow(
                "verified_user",
                t("customer.account.account_type"),
                <span class="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                  {customer.value.anon
                    ? t("customer.account.guest")
                    : t("customer.account.registered")}
                </span>,
              )}
              {renderRow(
                "fingerprint",
                t("customer.account.customer_id"),
                customer.value.id,
                "break-all font-mono text-xs font-normal text-base-content/60",
              )}
            </div>
          </section>

          {!customer.value.anon && (
            <button
              type="button"
              class="flex w-full items-center justify-center gap-2 rounded-2xl bg-error/10 px-4 py-3.5 text-[15px] font-semibold text-error transition-colors hover:bg-error/15 disabled:opacity-60"
              disabled={loggingOut.value}
              onClick={handleLogout}
            >
              {loggingOut.value ? (
                <span class="loading loading-spinner loading-sm"></span>
              ) : (
                <span
                  class="material-symbols-outlined text-xl"
                  aria-hidden="true"
                >
                  logout
                </span>
              )}
              {t("customer.collected.logout")}
            </button>
          )}
        </div>
      );
    };
  },
});
